import { DashboardAutoRedirectWrapper } from '@/components-v2/dashboard-auto-redirect-wrapper';
import CssBaseline from '@material-ui/core/CssBaseline';
import StylesProvider from '@material-ui/styles/StylesProvider';
import MuiThemeProvider from '@material-ui/styles/ThemeProvider';

import AffiliateLinkClaim from 'components-v2/affiliate-link-claim';
import Segmentation from 'components-v2/segmentation/Segmentation';
import Theme from 'components/layout/Theme';
import RemindPaymentModal from 'components/mocules/RemindPaymentModal';
import { DEFAULT_THUOCSI_DESCRIPTION, DEFAULT_THUOCSI_LONG_TITLE, DEFAULT_THUOCSI_TITLE, VISITED_URLS_STORAGE_KEY } from 'constants/data';
import { MOBILE } from 'constants/Device';
import { DOMAINS_THUOCSI } from 'constants/Paths';
import { AuthProvider, LoadingRoute } from 'context/Auth';
import { CartContextProvider } from 'context/Cart/CartContext';
import ContextProviderComposer from 'context/ContextProviderComposer';
import { NotiContextProvider } from 'context/Notifications';
import { PopupProvider } from 'context/Popup';
import { PopupVoucherProvider } from 'context/PopupVoucher';
import { ProductContextProvider } from 'context/Product/context';
import { SettingProvider } from 'context/Settings';
import { TicketProvider } from 'context/Ticket';
import { usePreventCapture } from 'hooks/usePreventCapture';
import App from 'next/app';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect, useRef } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider as StyledTheme } from 'styled-components';
import { DOMAIN_WEB_HOST, ENV } from 'sysconfig';
import gtag, { GA_TRACKING_ID_V2 } from 'utils/gtag';
import { getLinkCDNImage } from 'utils/ImageUtils';
import MonitorUtils from 'utils/MonitorUtils';
import { Provider, useCreateStore } from 'zustand-lib/storeGlobal';
import useMobileV2 from 'zustand-lib/storeMobile';
import '../styles/globals.css';
import '../styles/icomoon.css';
import '../styles/jodit.min.css';

const ChatThuocSi = dynamic(() => import('utils/chat-thuocsi/ChatThuocsi'), { ssr: false });
const Insider = dynamic(() => import('components-v2/tracking-script/Insider'), { ssr: false });

const PLATFORM = 'WEB';
const DOMAIN = 'thuocsi.vn';

const { publicRuntimeConfig } = getConfig();

const pathNoRedirect = [
  '/',
  '/list-product',
  '',
  '/flagship-store',
  '/flagship-store/[slug]/list-product',
  '/flagship-store/[slug]',
  '/flagship-store/durex',
  '/flagship-store/durex/list-product',
  '/flagship-store/durex/rewards',
  '/flagship-store/sanofi',
  '/flagship-store/sanofi/list-product',
  '/flagship-store/sanofi/rewards',
  '/phanhoi',
  '/qr',
  '/qr/[code]',
  'phanhoi',
  'feedback',
  '/feedback',
  '/flagship-store/[slug]/flagship-rewards',
  '/flagship-store/[slug]/rewards',
  '/tracking-order',
  '/maintain',
];

const WHITELIST_PATH_WILL_HIDDEN_CHAT = ['/partner-redirect'];

function MyApp(props) {
  const { Component, pageProps, host = '' } = props;

  const { user = {}, SEO_CONFIG = {}, initialZustandState = {} } = pageProps || {};

  const { thumbnailMap = {}, defaultThumbnail = {} } = initialZustandState || {};

  const router = useRouter();
  const { query, pathname } = router || {};
  const { utm_source: utmSource, utm_medium: utmMedium, utm_campaign: utmCampaign } = query || {};

  const reffererUrl = useRef('');
  const createStore = useCreateStore({ ...pageProps.initialZustandState, user });
  const { referCode, register, employeeId, action, login, forgetpasscode, t, token, redirectUrl } = query || {};
  const isShowingLogin = login === 'true';
  const isMobileV2 = useMobileV2((state) => state.isMobileV2());
  const isTrackingMobileV2 = useMobileV2((state) => state.isTrackingMobileV2);

  // signUpModal will auto open in 3 case
  // 1. In case user have referCode => auto open modal and fill referCode
  // 2. No need referCode => use "register" query => auto open modal
  // 3. Deep-link with "employeeId" (SSO referral) => auto open modal and fill referrerPhoneOrCode
  const defaultShowSignUpModal = !!referCode || !!register || !!employeeId;

  // Dùng SEO default là thông tin SEO của trang chủ
  const seoInfo = thumbnailMap[router.asPath] || {};

  // Generate Canonical URL
  const { pathname: canonicalPath } = new URL(router?.asPath || '/', 'http://localhost'); // chỉ lấy pathname, bỏ query params / hash
  const canonicalUrl = process.env.NEXT_PUBLIC_WEB_HOST + canonicalPath;
  const canonicalUrlMobile = process.env.NEXT_PUBLIC_WEB_M_HOST + canonicalPath;

  usePreventCapture();

  // Load UTM tracking params
  // If user arrives via a new campaign (utm_source exists):
  //   - Clear old localStorage UTM values
  //   - Save current URL as the original registerURL
  // Otherwise, keep existing stored campaign data
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hasUTM = Boolean(utmSource);

      if (hasUTM) {
        ['utm_source', 'utm_medium', 'utm_campaign', 'registerURL'].forEach((k) => localStorage.removeItem(k));
        localStorage.setItem('registerURL', window.location.href || '');
      }
    }

    const orderLead = {
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
    };

    Object.entries(orderLead).forEach(([key, value]) => {
      if (value) {
        localStorage.setItem(key, String(value));
      }
    });
  }, [utmSource, utmMedium, utmCampaign]);

  useEffect(() => {
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }

    // nếu khách vào thuocsi.com.vn -> cần redirect lại domain chính
    if (window && window?.location?.href?.indexOf('thuocsi.com.vn') >= 0) {
      router.push(`${DOMAIN_WEB_HOST}`);
    }

    // Prevent pull to refresh for chat mobile
    if (router.pathname === '/conversations') {
      document.querySelector('html')?.classList.add('preventPullToRefresh');
    } else {
      document.querySelector('html')?.classList?.remove('preventPullToRefresh');
    }
  }, []);

  const saveUrlWhenChangeRoute = () => {
    try {
      const visitedUrls = JSON.parse(localStorage.getItem(VISITED_URLS_STORAGE_KEY) || '[]');

      const currentUrl = window?.location?.href;
      if (currentUrl && currentUrl?.startsWith('http')) {
        let newVisitedUrls = currentUrl;

        if (visitedUrls[visitedUrls.length - 1] !== currentUrl) {
          newVisitedUrls = [...visitedUrls, currentUrl];
        }

        if (newVisitedUrls.length > 5) {
          // Chỉ lấy 5 urls gần nhất
          newVisitedUrls = newVisitedUrls.slice(newVisitedUrls.length - 5, newVisitedUrls.length);
        }

        localStorage.setItem(VISITED_URLS_STORAGE_KEY, JSON.stringify(newVisitedUrls));
      }
    } catch (e) {
      localStorage.setItem(VISITED_URLS_STORAGE_KEY, '[]');
    }
  };

  // DOMAINS_THUOCSI -> domain thuocsi sẽ chạy, các host khác proxy tới thuocsi sẽ bị đá lại domain chính
  useEffect(() => {
    if (DOMAINS_THUOCSI?.indexOf(host) === -1 && !pathNoRedirect.includes(router.pathname)) {
      router.push(`${DOMAIN_WEB_HOST}${router.asPath}`);
    }
    saveUrlWhenChangeRoute();
  }, [router.asPath]);

  useEffect(() => {
    const handleRouteChange = async (url) => {
      gtag.pageview(url, user);

      // Kiểm tra xem URL mới có phải thuộc seller home không
      const isSellerPath = url.match(/^\/(seller|flagship-store|seller-products|doitac)\/[^/]+/);
      const isFromSellerPath = reffererUrl.current.match(/^\/(seller|flagship-store|seller-products|doitac)\/[^/]+/);

      // Chỉ gửi event nếu:
      // 1. Không phải đang vào seller home
      // 2. Hoặc đang rời khỏi seller home (url mới không phải seller path nhưng url cũ là seller path)
      if (!isSellerPath || (isFromSellerPath && !isSellerPath)) {
        MonitorUtils.sendPageEvent({ url, reffererUrl: reffererUrl.current });
      }
      reffererUrl.current = url;
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);



  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta httpEquiv="Cache-Control" content="no-cache" />
        <meta httpEquiv="Expires" content="-1" />
        <meta name="keywords" content="thuốc sỉ" />
        <title>{seoInfo?.pageTitle || SEO_CONFIG?.title || defaultThumbnail?.pageTitle || DEFAULT_THUOCSI_LONG_TITLE}</title>
        <meta name="title" content={seoInfo?.title || SEO_CONFIG?.title || defaultThumbnail?.title || DEFAULT_THUOCSI_TITLE} />
        <meta
          name="description"
          content={seoInfo?.description || SEO_CONFIG?.description || defaultThumbnail?.description || DEFAULT_THUOCSI_DESCRIPTION}
        />

        <meta property="og:site_name" content={DEFAULT_THUOCSI_TITLE} />
        <meta property="og:title" content={seoInfo?.title || SEO_CONFIG?.ogTitle || defaultThumbnail?.title || DEFAULT_THUOCSI_TITLE} />
        <meta property="og:image" content={getLinkCDNImage(seoInfo?.imageUrl || SEO_CONFIG?.ogImage || defaultThumbnail?.imageUrl)} />
        <meta property="og:image:secure_url" content={getLinkCDNImage(seoInfo?.imageUrl || SEO_CONFIG?.ogImage || defaultThumbnail?.imageUrl)} />
        <meta property="og:image:alt" content={seoInfo?.title || SEO_CONFIG?.ogImageAlt || defaultThumbnail?.title || DEFAULT_THUOCSI_LONG_TITLE} />
        <meta
          property="og:description"
          content={seoInfo?.description || SEO_CONFIG?.ogDescription || defaultThumbnail?.description || DEFAULT_THUOCSI_DESCRIPTION}
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, minimum-scale=1.0" />

        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" media="only screen and (max-width: 640px)" href={canonicalUrlMobile} />

        <script type="application/ld+json">
          {`{
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "thuocsi.vn",
            "description": "thuocsi.vn được thành lập từ năm 2018, là một trong những startup thành công dẫn đầu trong lĩnh vực công nghệ về y tế và dịch vụ y tế, cung cấp thuốc sỉ giá tốt.",
            "logo": "https://cdn-web-next.thuocsi.vn/images/logo/buymed-logo.svg",
            "@id": "https://thuocsi.vn/",
            "url": "https://thuocsi.vn/",
            "openingHours": "Mo-Su 08:00-20:00",
            "telephone": "1800 2038",
            "email": "hotro@buymed.com",
            "address": {
            "streetAddress": "8 Floor, Vincom Center Dong Khoi, 72 Le Thanh Ton Street, Ben Nghe Ward, District 1, Ho Chi Minh City",
            "addressLocality": "Ho Chi Minh City",
            "postalCode": "70000",
            "addressCountry": "Vietnam"
            },
            "sameAs": [
              "https://www.facebook.com/thuocsivn/",
              "https://zalo.me/1836616064223034882",
              "https://www.linkedin.com/company/thuocsivn/",
              "https://www.tiktok.com/@thuocsi.vietnam?"
                ]
              }`}
        </script>

      </Head>
      {/* <NextNprogress color="#29D" startPosition={0.3} stopDelayMs={200} height="3" options={{ showSpinner: false }} /> */}
      <StylesProvider injectFirst>
        <StyledTheme theme={Theme}>
          <MuiThemeProvider theme={Theme}>
            <CssBaseline />
            {/* Context composer */}
            <Provider createStore={createStore}>
              <ContextProviderComposer
                contextProviders={[
                  <AuthProvider
                    key="AuthProvider"
                    isShowingLogin={isShowingLogin}
                    defaultShowSignUpModal={defaultShowSignUpModal}
                    forgetpasscode={forgetpasscode}
                    action={action}
                    tokenv1={t}
                    token={token}
                    redirectUrl={redirectUrl}
                    // initAccount={accountInfo}
                    initUser={user}
                  />,
                  <ProductContextProvider key="ProductContextProvider" initUser={user} />,
                  <CartContextProvider key="CartContextProvider" />,
                  <NotiContextProvider key="NotiContextProvider" initUser={user} />,
                  <SettingProvider key="SettingProvider" />,
                  <TicketProvider key="TicketProvider" />,
                  <PopupProvider key="PopupProvider" />,
                ]}
              >
                <LoadingRoute>
                  <DashboardAutoRedirectWrapper isAuthenticated={pageProps?.isAuthenticated}>
                    <PopupVoucherProvider>
                      <Component {...pageProps} />
                    </PopupVoucherProvider>
                  </DashboardAutoRedirectWrapper>

                  <RemindPaymentModal />
                  <AffiliateLinkClaim />
                  {WHITELIST_PATH_WILL_HIDDEN_CHAT.some((path) => router.pathname.includes(path)) ? null : (
                    <ChatThuocSi key={pageProps?.isAuthenticated || null} />
                  )}
                  <ToastContainer limit={2} pauseOnHover={false} hideProgressBar autoClose={2000} closeOnClick />
                </LoadingRoute>
              </ContextProviderComposer>
            </Provider>
          </MuiThemeProvider>
        </StyledTheme>
      </StylesProvider>

      <Insider host={host} user={user} zustandState={initialZustandState} />
      <Segmentation />

      {ENV === 'prd' && isMobileV2 && isTrackingMobileV2 && (
        <>
          <Script strategy="lazyOnload" src={`https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID_V2}&l=dataLayerGAV2`} />
          <Script strategy="lazyOnload">
            {`
                    window.dataLayerGAV2 = window.dataLayerGAV2 || [];
                    function gtagGAV2(){dataLayerGAV2.push(arguments);}
                    gtagGAV2('js', new Date());
                    gtagGAV2('config', '${GA_TRACKING_ID_V2}', {
                    page_path: window.location.pathname,
                    });
                `}
          </Script>
        </>
      )}
    </>
  );
}

MyApp.getInitialProps = async (appContext) => {


  const time = +new Date();

  const appProps = await App.getInitialProps(appContext);

  let host = '';
  try {
    host = appContext.ctx.req.headers.host || '';
  } catch (error) {
    host = "can't detect host";
  }

  let isMobile = '';
  try {
    const UA = appContext.ctx.req.headers['user-agent'];
    isMobile = Boolean(UA.match(MOBILE));
  } catch (error) {
    isMobile = `can not detect device - ${error}`;
  }

  return {
    ...appProps,
    host,
    buildId: publicRuntimeConfig.buildId,
    timeAPP: +new Date() - time,

    pageProps: {
      isMobile: !!isMobile,
    },
  };
};

export default MyApp;
